// Tenses & Moods — a complete reference for each Portuguese tense with uses,
// the falar pattern, example sentences, and a tip. European Portuguese.
import { speakButtonHTML } from "../lib/audio.js";

const SECTIONS = [
  {
    id: "presente",
    mood: "Indicative",
    title: "Presente (Present)",
    icon: "☀️",
    color: "bg-sage-50 border-sage-200",
    uses: "Current actions, habitual actions, general truths, and near-future plans.",
    examples: [
      { pt: "Falo português todos os dias.", en: "I speak Portuguese every day." },
      { pt: "O sol nasce a este.", en: "The sun rises in the east." },
      { pt: "Amanhã vou ao ginásio.", en: "Tomorrow I'm going to the gym." },
      { pt: "O que estás a fazer?", en: "What are you doing?" },
    ],
    pattern: "falo · falas · fala · falamos · falais · falam",
    tip: "Portuguese marks the progressive with <b>estar a + infinitive</b> (estou a falar) in Portugal — for an action happening right now in the moment.",
  },
  {
    id: "preterito-perfeito",
    mood: "Indicative",
    title: "Pretérito Perfeito (Simple Past)",
    icon: "📅",
    color: "bg-sand-50 border-sand-200",
    uses: "Completed actions at a specific moment in the past — the storyline events.",
    examples: [
      { pt: "Ontem comi pizza.", en: "Yesterday I ate pizza." },
      { pt: "Chegámos às oito.", en: "We arrived at eight." },
      { pt: "No ano passado viajei para o México.", en: "Last year I travelled to Mexico." },
      { pt: "Ela nasceu em 1990.", en: "She was born in 1990." },
    ],
    pattern: "falei · falaste · falou · falámos · falastes · falaram",
    tip: "Each event has a defined start and end. Common signals: ontem, anteontem, a semana passada, em 2010. Note the accent in <i>falámos</i> (EP) vs the present <i>falamos</i>.",
  },
  {
    id: "preterito-imperfeito",
    mood: "Indicative",
    title: "Pretérito Imperfeito (Imperfect)",
    icon: "🎞️",
    color: "bg-sage-50 border-sage-200",
    uses: "Ongoing, repeated, or background actions in the past — the scenery, not the events.",
    examples: [
      { pt: "Quando era criança, jogava futebol.", en: "When I was a child, I used to play football." },
      { pt: "Chovia enquanto caminhávamos.", en: "It was raining while we were walking." },
      { pt: "A minha avó cozinhava todos os domingos.", en: "My grandmother used to cook every Sunday." },
      { pt: "Eram três da tarde.", en: "It was three in the afternoon." },
    ],
    pattern: "falava · falavas · falava · falávamos · faláveis · falavam",
    tip: "Used for \"used to\", \"was -ing\", descriptions, age, time, weather, and feelings in the past. Only four verbs are irregular: ser, ter, vir, pôr.",
  },
  {
    id: "perfeito-vs-imperfeito",
    mood: "Contrast",
    title: "Pretérito Perfeito vs Imperfeito",
    icon: "⚖️",
    color: "bg-amber-50 border-amber-200",
    uses: "The hardest contrast for English speakers. Both translate as English past tense, but they tell different stories.",
    examples: [
      { pt: "Comia quando ligaste.", en: "I was eating when you called. (background + event)" },
      { pt: "Comi quando ligaste.", en: "I ate when you called. (two events in sequence)" },
      { pt: "Conheci a Maria em 2010.", en: "I met Maria in 2010. (one moment)" },
      { pt: "Conhecia a Maria.", en: "I knew/used to know Maria. (ongoing state)" },
    ],
    pattern: "Perfeito = WHAT happened · Imperfeito = WHAT WAS HAPPENING / HOW it was",
    tip: "If you can rewrite it in English as \"was -ing\" or \"used to\", it's almost always imperfeito. A single completed event is perfeito.",
  },
  {
    id: "mais-que-perfeito",
    mood: "Indicative",
    title: "Pretérito Mais-que-Perfeito (Pluperfect)",
    icon: "⏪",
    color: "bg-sand-50 border-sand-200",
    uses: "An action that happened BEFORE another past action — \"had done\" in English. The simple (synthetic) form is literary; speech prefers the compound tinha + particípio.",
    examples: [
      { pt: "Quando cheguei, eles já tinham comido.", en: "When I arrived, they had already eaten. (compound, everyday)" },
      { pt: "Nunca vira nada assim.", en: "I had never seen anything like that. (simple, literary)" },
      { pt: "Disse-me que já terminara o trabalho.", en: "He told me he had already finished the work." },
    ],
    pattern: "Simples: falara · falaras · falara · faláramos · faláreis · falaram  ·  Composto: tinha falado",
    tip: "The simple form (falara) is mostly written/formal. In conversation use <b>tinha</b> (imperfect of ter) + past participle: tinha falado.",
  },
  {
    id: "futuro",
    mood: "Indicative",
    title: "Futuro (Future)",
    icon: "🚀",
    color: "bg-sage-50 border-sage-200",
    uses: "Predictions, promises, and probability/conjecture in the present.",
    examples: [
      { pt: "Amanhã falarei com ele.", en: "Tomorrow I will speak with him." },
      { pt: "Nunca esqueceremos este dia.", en: "We will never forget this day." },
      { pt: "Onde estará a Maria?", en: "I wonder where Maria is. (probability)" },
      { pt: "Serão dez horas.", en: "It must be about ten o'clock." },
    ],
    pattern: "falarei · falarás · falará · falaremos · falareis · falarão",
    tip: "In daily speech, Portuguese usually uses <b>ir + infinitive</b> instead: \"vou falar\" (I'm going to speak). Only fazer, dizer and trazer have contracted stems (farei, direi, trarei).",
  },
  {
    id: "condicional",
    mood: "Indicative",
    title: "Condicional (Conditional)",
    icon: "🤔",
    color: "bg-sage-50 border-sage-200",
    uses: "Polite requests, hypothetical situations, the \"would\" of English. Also called futuro do pretérito.",
    examples: [
      { pt: "Gostaria de um café.", en: "I would like a coffee." },
      { pt: "Podias ajudar-me?", en: "Could you help me? (imperfect often replaces it in speech)" },
      { pt: "No teu lugar, não iria.", en: "If I were you, I wouldn't go." },
      { pt: "Disse que chegaria tarde.", en: "He said he would arrive late." },
    ],
    pattern: "falaria · falarias · falaria · falaríamos · falaríeis · falariam",
    tip: "Built on the whole infinitive + imperfect endings (-ia…), sharing the irregular stems of the future (fazer → far-, dizer → dir-). In casual speech the imperfect (falava) often stands in for it.",
  },
  {
    id: "presente-conjuntivo",
    mood: "Subjunctive",
    title: "Presente do Conjuntivo (Present Subjunctive)",
    icon: "💭",
    color: "bg-violet-50 border-violet-200",
    uses: "Doubt, desire, emotion, denial, hypotheticals — anything that isn't a stated fact.",
    examples: [
      { pt: "Quero que venhas.", en: "I want you to come." },
      { pt: "É importante que estudes.", en: "It's important that you study." },
      { pt: "Não acho que seja verdade.", en: "I don't think it's true." },
      { pt: "Espero que tenhas um bom dia.", en: "I hope you have a good day." },
      { pt: "Talvez chova amanhã.", en: "It might rain tomorrow." },
    ],
    pattern: "fale · fales · fale · falemos · faleis · falem",
    tip: "Built from the <b>eu</b> present indicative with \"opposite\" endings (-ar takes -e endings, -er/-ir take -a endings). Yo-style irregularities carry over: faço → faça, ponho → ponha.",
  },
  {
    id: "imperfeito-conjuntivo",
    mood: "Subjunctive",
    title: "Pretérito Imperfeito do Conjuntivo (Imperfect Subjunctive)",
    icon: "🌙",
    color: "bg-violet-50 border-violet-200",
    uses: "Past-tense subjunctive: hypothetical, counterfactual, polite expressions, and clauses triggered by past-tense main verbs.",
    examples: [
      { pt: "Se tivesse dinheiro, viajaria.", en: "If I had money, I would travel." },
      { pt: "Queria que viesses.", en: "I wanted you to come." },
      { pt: "Era importante que chegasses a tempo.", en: "It was important that you arrived on time." },
      { pt: "Se eu fosse rico…", en: "If I were rich…" },
    ],
    pattern: "falasse · falasses · falasse · falássemos · falásseis · falassem",
    tip: "One single form in Portuguese (-sse), unlike Spanish's two. Built from the <b>eles</b> preterite minus -ram (falaram → fala- → falasse; fizeram → fizesse).",
  },
  {
    id: "futuro-conjuntivo",
    mood: "Subjunctive",
    title: "Futuro do Conjuntivo (Future Subjunctive)",
    icon: "🔮",
    color: "bg-violet-50 border-violet-200",
    uses: "A living, everyday Portuguese tense with NO Spanish equivalent: hypothetical future events after se, quando, logo que, assim que, enquanto, conforme.",
    examples: [
      { pt: "Quando chegares, liga-me.", en: "When you arrive, call me." },
      { pt: "Se quiseres, podemos sair.", en: "If you want, we can go out." },
      { pt: "Faremos isso assim que pudermos.", en: "We'll do that as soon as we can." },
      { pt: "Logo que souberes, avisa.", en: "As soon as you know, let me know." },
    ],
    pattern: "falar · falares · falar · falarmos · falardes · falarem",
    tip: "Built from the <b>eles</b> preterite minus -am (falaram → falar-; fizeram → fizer-; foram → for-). For regular verbs it looks just like the infinitive, but irregular verbs reveal the strong stem: quando <i>fores</i>, se <i>fizeres</i>.",
  },
  {
    id: "infinitivo-pessoal",
    mood: "Non-finite",
    title: "Infinitivo Pessoal (Personal Infinitive)",
    icon: "🪪",
    color: "bg-sage-50 border-sage-200",
    uses: "Portuguese's signature feature: an infinitive that inflects for its own subject. Used after prepositions and impersonal expressions when the infinitive has a clear subject.",
    examples: [
      { pt: "É melhor saíres agora.", en: "It's better for you to leave now." },
      { pt: "Trouxe um livro para lermos.", en: "I brought a book for us to read." },
      { pt: "Ao chegarem, telefonaram.", en: "Upon arriving, they phoned." },
      { pt: "Obrigado por virem.", en: "Thank you (all) for coming." },
    ],
    pattern: "falar · falares · falar · falarmos · falardes · falarem",
    tip: "Always regular: infinitive + (-, -es, -, -mos, -des, -em), even for irregular verbs (fazermos, sermos). Use it when each \"to-\" action has its own subject; otherwise the plain infinitive is enough.",
  },
  {
    id: "imperativo",
    mood: "Imperative",
    title: "Imperativo (Commands)",
    icon: "❗",
    color: "bg-rose-50 border-rose-200",
    uses: "Direct commands and instructions.",
    examples: [
      { pt: "Fala mais devagar!", en: "Speak more slowly! (tu)" },
      { pt: "Não fales tão depressa.", en: "Don't speak so fast. (tu)" },
      { pt: "Comamos agora.", en: "Let's eat now." },
      { pt: "Sente-se, por favor.", en: "Please sit down. (você, formal)" },
    ],
    pattern: "Affirmative tu = 3rd-sing present (fala) · Negative tu = conjuntivo (não fales) · você / nós / vocês all use the conjuntivo",
    tip: "Only the affirmative <b>tu</b> is special (it looks like \"ele fala\"). Everything else — negative tu, você, nós, vós-negative, vocês — uses the present conjuntivo. With pronouns: affirmative attaches (senta-te), negative precedes (não te sentes).",
  },
  {
    id: "perfeito-composto",
    mood: "Compound",
    title: "Pretérito Perfeito Composto (Present Perfect)",
    icon: "🔁",
    color: "bg-sand-50 border-sand-200",
    uses: "A repeated or continuous action running from the past up to now — NOT a one-off completed event.",
    examples: [
      { pt: "Tenho falado com ele esta semana.", en: "I have been speaking with him this week." },
      { pt: "Ultimamente tem chovido muito.", en: "It has been raining a lot lately." },
      { pt: "Temos viajado bastante este ano.", en: "We have been travelling a lot this year." },
    ],
    pattern: "ter (present) + past participle: tenho/tens/tem/temos/tendes/têm + falado",
    tip: "False friend alert: \"tenho falado\" ≠ English \"I have spoken (once)\". It means an ongoing/repeated habit. For a single finished past action, use the pretérito perfeito (falei).",
  },
  {
    id: "mais-que-perfeito-composto",
    mood: "Compound",
    title: "Mais-que-Perfeito Composto (Past Perfect)",
    icon: "⏮️",
    color: "bg-sand-50 border-sand-200",
    uses: "The everyday \"had done\" — one past action completed before another.",
    examples: [
      { pt: "Quando cheguei, já tinham comido.", en: "When I arrived, they had already eaten." },
      { pt: "Nunca tinha visto nada assim.", en: "I had never seen anything like that." },
      { pt: "Disse-me que tinha terminado.", en: "He told me he had finished." },
    ],
    pattern: "ter (imperfect) + past participle: tinha/tinhas/tinha/tínhamos/tínheis/tinham + falado",
    tip: "This compound (tinha + particípio) is the normal spoken pluperfect; the one-word form (terminara) is reserved for writing.",
  },
];

export function tensesView(app) {
  const tocItems = SECTIONS.map(
    (s) => `
    <a href="#${s.id}" class="block text-sm text-sage-600 hover:text-sage-800 py-1">
      ${s.icon} ${s.title}
    </a>`
  ).join("");

  const sectionHTML = SECTIONS.map(
    (s) => `
    <article id="${s.id}" class="${s.color} border rounded-2xl p-5 scroll-mt-32">
      <div class="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h3 class="font-bold text-lg text-sage-800">${s.icon} ${s.title}</h3>
        <span class="text-xs px-2 py-1 rounded-full bg-white/70 text-sage-600 font-medium">${s.mood}</span>
      </div>
      <p class="text-sm text-sage-700 mb-3"><b>When to use:</b> ${s.uses}</p>
      <div class="bg-white/60 rounded-lg p-3 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500 mb-1">Pattern (falar)</p>
        <p class="font-mono text-xs text-sage-700 leading-relaxed">${s.pattern}</p>
      </div>
      <div class="space-y-2 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500">Examples</p>
        ${s.examples
          .map(
            (ex) => `
          <div class="bg-white/60 rounded-lg p-2.5 text-sm">
            <p class="text-sage-800 flex items-start gap-1.5"><b>${ex.pt}</b>${speakButtonHTML(ex.pt)}</p>
            <p class="text-sage-500 text-xs italic mt-0.5">${ex.en}</p>
          </div>`
          )
          .join("")}
      </div>
      <p class="text-xs text-sage-600 leading-relaxed bg-white/40 rounded-lg p-2.5">💡 ${s.tip}</p>
    </article>
  `
  ).join("");

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Tenses & Moods</h2>
        <p class="text-sage-600 text-sm">A complete guide to Portuguese verb tenses — when to use each, with example sentences.</p>
      </div>

      <div class="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside class="bg-white rounded-2xl border border-sage-100 p-4 self-start lg:sticky lg:top-4">
          <p class="text-xs uppercase tracking-wide text-sage-500 font-medium mb-2">Jump to</p>
          <nav>${tocItems}</nav>
        </aside>

        <div class="space-y-4">
          <article class="bg-gradient-to-br from-sage-50 to-sand-50 border border-sage-100 rounded-2xl p-5">
            <h3 class="font-bold text-sage-800 mb-2">🗺️ The big picture</h3>
            <p class="text-sm text-sage-700 mb-2">Portuguese verbs encode <b>three moods</b> plus inflected non-finite forms:</p>
            <ul class="text-sm text-sage-700 space-y-1.5 list-disc pl-5">
              <li><b>Indicative</b> — states facts: <i>presente, pretérito perfeito, imperfeito, mais-que-perfeito, futuro, condicional</i>.</li>
              <li><b>Subjunctive (conjuntivo)</b> — doubt, desire, the unreal: <i>presente, imperfeito,</i> and the distinctly Portuguese <i>futuro do conjuntivo</i>.</li>
              <li><b>Imperative</b> — commands: <i>fala, não fales</i>.</li>
              <li><b>Personal infinitive</b> — an infinitive that inflects for its subject: <i>para falarmos</i>.</li>
            </ul>
            <p class="text-sm text-sage-700 mt-2">Compound tenses pair <i>ter</i> with a past participle: <i>tenho falado</i>, <i>tinha falado</i>.</p>
          </article>

          ${sectionHTML}

          <article class="bg-white rounded-2xl border border-sage-100 p-5">
            <h3 class="font-bold text-sage-800 mb-3">🧭 Choosing the right tense — quick decision tree</h3>
            <div class="text-sm text-sage-700 space-y-2">
              <p><b>Is the action happening now or generally true?</b> → Presente</p>
              <p><b>Did it happen at a specific moment in the past?</b> → Pretérito Perfeito</p>
              <p><b>Was it ongoing, repeated, or descriptive in the past?</b> → Imperfeito</p>
              <p><b>Will it happen?</b> → Futuro (or <i>ir +</i> infinitive in casual speech)</p>
              <p><b>Would you do it?</b> → Condicional</p>
              <p><b>Does the sentence express a wish, doubt, emotion, or hypothetical?</b> → Conjuntivo</p>
              <p><b>A future "when/if" clause (quando, se, assim que)?</b> → Futuro do Conjuntivo</p>
              <p><b>Are you giving a command?</b> → Imperativo</p>
              <p><b>"Have been doing" / "had done"?</b> → Compound tenses with <i>ter</i></p>
            </div>
          </article>
        </div>
      </div>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
